import { Injectable } from "@angular/core";
import { SharedService } from "../_shared/shared.service";
import { HttpPlus } from "../SayTS/http-plus";
import {
  Location,
  ILocation,
  LocationType,
} from "../_models/location/Location";
import { LocationFactory } from "../_models/location/LocationFactory";
import { LocationDto } from "../_models/location/LocationDTO";

@Injectable({
  providedIn: "root",
})
export class LocationStore {
  private locations: Location[] = [];
  private initPromise: Promise<void> | null = null;

  constructor(
    private sharedService: SharedService,
    private httpPlus: HttpPlus,
  ) {
    this.init();
  }

  init(): Promise<void> {
    if (!this.initPromise) {
      this.initPromise = this._load();
    }
    return this.initPromise;
  }

  private async _load(): Promise<void> {
    const locs = await this.httpPlus.get(`location`);
    if (locs) {
      for (const l of locs as any as Iterable<LocationDto>) {
        try {
          this.locations.push(LocationFactory.FromDto(LocationDto.fromJson(l)));
        } catch (e) {
          // Skip a malformed location row instead of aborting the whole list.
          // A throw here would reject the cached init() promise permanently,
          // making every consumer's getLocation() reject and flooding the
          // backend with errorJs reports (one per battery card).
          console.warn("LocationStore: skipping unparseable location", l, e);
        }
      }
    }
  }

  async getLocation(id: number): Promise<Location | undefined> {
    await this.init();
    let loc = this.locations.find((l) => l.id_location === id);

    if (!loc) {
      const loc_json = await this.httpPlus.get(`location/${id}`, {
        hideLoader: true,
      });
      if (loc_json) {
        this.locations.push(
          LocationFactory.FromDto(LocationDto.fromJson(loc_json)),
        );
        loc = this.locations.find((l) => l.id_location === id);
      }
    }

    return loc;
  }

  async reloadLocation(id: number): Promise<void> {
    const loc_json = await this.httpPlus.get(`location/${id}`, {
      hideLoader: true,
    });
    let loc = this.locations.find((l) => l.id_location === id);
    if (loc_json) {
      this.locations.push(
        LocationFactory.FromDto(LocationDto.fromJson(loc_json)),
      );
    }
    if (loc) {
      loc.FromDto(LocationDto.fromJson(loc_json));
    }
  }

  async getLocations(): Promise<Location[]> {
    await this.init();
    return this.locations;
  }

  /**
   * Znovu stáhne kompletní seznam lokací (auto-refresh dashboardu upozornění).
   * Nová data se přehodí až po úspěšném stažení — při chybě zůstává stávající
   * seznam, aby UI „neprobliklo" do prázdna.
   */
  async reloadAll(hideLoader = true): Promise<Location[]> {
    const locs = await this.httpPlus.get(`location`, null, hideLoader);
    if (locs) {
      const fresh: Location[] = [];
      for (const l of locs as any as Iterable<LocationDto>) {
        try {
          fresh.push(LocationFactory.FromDto(LocationDto.fromJson(l)));
        } catch (e) {
          console.warn("LocationStore: skipping unparseable location", l, e);
        }
      }
      this.locations = fresh;
      this.initPromise = Promise.resolve();
    }
    return this.locations;
  }
}
